/**
 * ContactSearch Component
 *
 * A searchable dropdown for selecting existing contacts (companies)
 * or entering new contact information manually.
 */

import { Building, Check, Loader2, Search, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useCompanySearch } from '@/features/contacts/hooks/useCompaniesQueries';
import type { Company } from '@/features/contacts/types';
import { cn } from '@/lib/utils';

export interface ContactData {
  name: string;
  email: string;
  phone: string;
  address_street: string;
  address_city: string;
  address_state: string;
  address_zip: string;
}

interface ContactSearchProps {
  /** Current contact data */
  value: ContactData;
  /** Callback when contact data changes */
  onChange: (data: ContactData) => void;
  /** Placeholder text for the search input */
  placeholder?: string;
  /** Label for the party type (e.g., "Buyer", "Seller", "Lender") */
  partyLabel?: string;
  /** Whether to show the address fields when prefilling */
  includeAddress?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Disable the search functionality */
  disabled?: boolean;
}

/**
 * Maps a Company object to ContactData format
 */
function companyToContactData(company: Company, includeAddress: boolean): ContactData {
  return {
    name: company.name || company.primary_contact_name || '',
    email: company.email || company.primary_contact_email || '',
    phone: company.phone || '',
    address_street: includeAddress ? company.address_street || '' : '',
    address_city: includeAddress ? company.address_city || '' : '',
    address_state: includeAddress ? company.address_state || '' : '',
    address_zip: includeAddress ? company.address_zip || '' : '',
  };
}

export function ContactSearch({
  value,
  onChange,
  placeholder = 'Search contacts...',
  partyLabel = 'Contact',
  includeAddress = true,
  className,
  disabled = false,
}: ContactSearchProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Search companies using the debounced query
  const {
    data: searchResults,
    isLoading,
    isFetching,
  } = useCompanySearch(debouncedQuery, 10, {
    enabled: debouncedQuery.length >= 2,
  });

  const handleSelectCompany = (company: Company) => {
    const contactData = companyToContactData(company, includeAddress);
    onChange(contactData);
    setOpen(false);
    setSearchQuery('');
  };

  const handleEnterManually = () => {
    // Just close the popover, user will enter data in the form
    setOpen(false);
    setSearchQuery('');
  };

  const hasValue = value.name.trim().length > 0;
  const showLoading = isLoading || isFetching;
  const showResults = debouncedQuery.length >= 2 && searchResults && searchResults.length > 0;
  const showEmpty =
    debouncedQuery.length >= 2 && !showLoading && (!searchResults || searchResults.length === 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal',
            !hasValue && 'text-muted-foreground',
            className,
          )}
          disabled={disabled}
        >
          <div className="flex items-center gap-2 truncate">
            <Search className="h-4 w-4 shrink-0 opacity-50" />
            {hasValue ? <span className="truncate">{value.name}</span> : <span>{placeholder}</span>}
          </div>
          {hasValue && <Check className="ml-2 h-4 w-4 shrink-0 text-green-500" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={`Search existing ${partyLabel.toLowerCase()}s...`}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {/* Loading state */}
            {showLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
              </div>
            )}

            {/* Empty state */}
            {showEmpty && <CommandEmpty>No contacts found for "{debouncedQuery}"</CommandEmpty>}

            {/* Search results */}
            {showResults && (
              <CommandGroup heading="Existing Contacts">
                {searchResults.map((company) => (
                  <CommandItem
                    key={company.id}
                    value={company.id}
                    onSelect={() => handleSelectCompany(company)}
                    className="cursor-pointer"
                  >
                    <Building className="mr-2 h-4 w-4 text-muted-foreground" />
                    <div className="flex flex-col">
                      <span className="font-medium">{company.name}</span>
                      {(company.address_city || company.phone) && (
                        <span className="text-xs text-muted-foreground">
                          {[
                            company.address_city && company.address_state
                              ? `${company.address_city}, ${company.address_state}`
                              : company.address_city || company.address_state,
                            company.phone,
                          ]
                            .filter(Boolean)
                            .join(' | ')}
                        </span>
                      )}
                    </div>
                    {value.name === company.name && (
                      <Check className="ml-auto h-4 w-4 text-green-500" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Initial state or separator before manual entry */}
            {(showResults || showEmpty) && <CommandSeparator />}

            {/* Manual entry option */}
            <CommandGroup>
              <CommandItem onSelect={handleEnterManually} className="cursor-pointer">
                <UserPlus className="mr-2 h-4 w-4 text-blue-500" />
                <div className="flex flex-col">
                  <span className="font-medium">Enter {partyLabel} Manually</span>
                  <span className="text-xs text-muted-foreground">
                    Create a new contact for this order
                  </span>
                </div>
              </CommandItem>
            </CommandGroup>

            {/* Hint when no search */}
            {debouncedQuery.length < 2 && !showLoading && (
              <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                Type at least 2 characters to search existing contacts
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
